import React, {useCallback, useState} from 'react';
import styled, {keyframes} from 'styled-components';
import {Image, ImageRatioOptions} from '../../images';
import {VerticalSpacer} from '../..';
import {Typography} from '../../typography';
import {useActiveCreator, useActiveDetailCard} from '../creatorfeed.hooks';
import {MouseHijack, WithCaption, WithCaptionProps} from '../../../wraps';
import {stopFocusOnClick} from 'epic-ue-shared';
import {AccentColorProps, AccentColorOptions} from 'epic-ue-theme';

export const fadeIn = keyframes`
    0% {
        opacity: 0;
    }
    100% {
        opacity: 1;
    }
`;

export const fadeOut = keyframes`
    0% {
        opacity: 1;
    }
    50% {
        opacity: 1;
    }
    100% {
        opacity: 0;
    }
`;

const ActiveBackground = styled.div`
    position: fixed;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 199;
    background-color: ${(p) => p.theme.palette.background.default};
    animation: ${fadeIn} 0.6s ${(p) => p.theme.easing};

    @media (prefers-reduced-motion: reduce) {
        animation: none;
    }
`;

const Styled = styled.div<{accentColor?: AccentColorOptions}>`
    ${(p) => p.theme.breakpoints.up('md')} {
        margin-right: 10px;
    }
    ${(p) => p.theme.breakpoints.up('lg')} {
        margin-right: 40px;
    }

    .ue-onclick-wrap {
        cursor: pointer;
    }

    a {
        transition: color 0.2s ${(p) => p.theme.easing};
        &:hover {
            color: ${(p) =>
                p.accentColor
                    ? p.theme.palette.accent[p.accentColor]
                    : p.theme.palette.text.primary};
        }
    }
`;

const ImageWrap = styled.div`
    .image-wrapper img {
        transition: transform 0.6s ${(p) => p.theme.easing};
        transform: scale3d(1, 1, 1);
    }

    &:hover {
        .image-wrapper img {
            transform: scale3d(1.05, 1.05, 1.05);
        }
    }

    &.active {
        opacity: 0;
        position: relative;
        z-index: 201;
        animation: ${fadeOut} 1.5s ${(p) => p.theme.easing};

        @media (prefers-reduced-motion: reduce) {
            animation: none;
        }
    }
`;

const Avatar = styled.div`
    display: inline-block;
    width: 56px;
    margin: 0 0 0 24px;
    img {
        border-radius: 56px;
    }
`;

const ContentWrap = styled.div`
    display: inline-block;
    position: relative;
    top: -8px;
    ${(p) => p.theme.breakpoints.up('md')} {
        width: calc(100% - 56px - 24px);
    }
    a {
        cursor: pointer;
    }

    .paragraph {
        color: ${(p) => p.theme.palette.text.secondary};
    }
`;

const Flex = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

export interface CreatorFeedCardProps {
    title?: string;
    caption?: string;
    imageSrc?: string;
    avatarImageSrc?: string;
    ratio?: ImageRatioOptions;
    imgCaption?: WithCaptionProps;
}

export const CreatorFeedCard = ({
    title,
    imageSrc,
    ratio,
    caption,
    avatarImageSrc,
    accentColor,
    imgCaption
}: CreatorFeedCardProps & AccentColorProps): JSX.Element => {
    const [active, setActive] = useState(false);
    const {setActiveCreator} = useActiveCreator();
    const {setActiveDetailCard} = useActiveDetailCard();
    const onClick = useCallback(
        (e) => {
            e.preventDefault();
            setActive(true);
            setActiveDetailCard(0);

            //allow animation to run before opening detail view
            setTimeout(() => {
                setActiveCreator(title);
                //after the detail view renders then reset the grid
                setTimeout(() => {
                    setActive(false);
                }, 1200);
            }, 900);
        },
        [setActiveCreator, setActive, title]
    );

    return (
        <Styled accentColor={accentColor}>
            {active ? <ActiveBackground /> : ''}
            <ImageWrap className={active ? 'active' : ''}>
                <MouseHijack onClick={onClick} hoverCursorType="view" accentColor={accentColor}>
                    <WithCaption {...imgCaption}>
                        <Image src={imageSrc} ratio={ratio} optMaxWidth={800} />
                    </WithCaption>
                </MouseHijack>
            </ImageWrap>
            <VerticalSpacer variant="custom" spacing={4} downSmSpacing={2} />
            <Flex>
                <ContentWrap>
                    <a
                        role="menuitem"
                        tabIndex={0}
                        onClick={onClick}
                        onMouseDown={stopFocusOnClick}>
                        <Typography variant="h3" big>
                            {title}
                        </Typography>
                    </a>
                    <Typography variant="p" caption>
                        {caption}
                    </Typography>
                </ContentWrap>
                <Avatar>
                    <Image
                        src={avatarImageSrc}
                        ratio="square"
                        fit="cover"
                        height={56}
                        width={56}
                        optMaxWidth={120}
                    />
                </Avatar>
            </Flex>
        </Styled>
    );
};
